import {useEffect, useState, useCallback, useRef} from 'react';
import {kr} from '../lib/format.js';
import {api} from '../lib/auth.js';

/**
 * The review surface for the reclassification.
 *
 * A bulk reclassification without one is a black box: 1 405 rows were mapped onto the
 * article register by rules, and the only honest way to ship that is to show which
 * rule made each decision, how sure it was, and let a human overrule it.
 *
 * The queue defaults to what actually needs looking at — unclassified first, then the
 * inferred ones — rather than to everything.
 */
const CONFIDENCE = [
	{key: 'OKLASSAT', label: 'Oklassat', hint: 'ingen regel träffade'},
	{key: 'low', label: 'Låg', hint: 'svag signal'},
	{key: 'medium', label: 'Medel', hint: 'härledd ur beskrivningen'},
	{key: 'high', label: 'Hög', hint: 'artikelnummer eller entydig term'},
	{key: '', label: 'Alla', hint: ''},
];

export default function Classify() {
	const [summary, setSummary] = useState(null);
	const [articles, setArticles] = useState([]);
	const [confidence, setConfidence] = useState('OKLASSAT');
	const [q, setQ] = useState('');
	const [page, setPage] = useState(1);
	const [data, setData] = useState({rows: [], total: 0, pages: 1, loading: true});
	const [saving, setSaving] = useState(null);
	const seq = useRef(0);

	const loadSummary = useCallback(() => {
		api('/api/classifications/summary').then(r => r.json()).then(setSummary).catch(() => {});
	}, []);
	useEffect(() => {
		loadSummary();
		api('/api/articles').then(r => r.json()).then(setArticles).catch(() => setArticles([]));
	}, [loadSummary]);
	useEffect(() => { setPage(1); }, [confidence, q]);

	const load = useCallback(async () => {
		const mine = ++seq.current;
		setData(d => ({...d, loading: true}));
		const p = new URLSearchParams({page: String(page), limit: '50'});
		if (confidence) p.set('confidence', confidence);
		if (q) p.set('q', q);
		try {
			const r = await (await api(`/api/classifications?${p}`)).json();
			if (mine === seq.current) setData({...r, loading: false});
		} catch { if (mine === seq.current) setData(d => ({...d, loading: false})); }
	}, [confidence, q, page]);
	useEffect(() => { const t = setTimeout(load, q ? 220 : 0); return () => clearTimeout(t); }, [load, q]);

	const override = async (id, article) => {
		setSaving(id);
		try {
			const res = await api(`/api/classifications/${encodeURIComponent(id)}`, {
				method: 'PUT', headers: {'Content-Type': 'application/json'},
				body: JSON.stringify({article: article || null}),
			});
			if (res.ok) {
				const doc = await res.json();
				setData(d => ({...d, rows: d.rows.map(r => (r._id === id ? {...r, ...doc} : r))}));
				loadSummary();
			}
		} finally { setSaving(null); }
	};

	const counts = new Map((summary?.by_confidence ?? []).map(c => [c._id, c]));

	return (
		<>
			<p className="sub">
				Alla {summary?.total ?? '…'} fakturarader 2021–2026, mappade mot artikelregistret.
				En manuell ändring är permanent — omklassificeringen hoppar över rader som rättats för hand.
			</p>

			<div className="toolbar">
				<div className="presets" role="group" aria-label="Säkerhet">
					{CONFIDENCE.map(c => {
						const n = c.key === '' ? summary?.total : counts.get(c.key)?.n;
						return (
							<button key={c.key} className="seg" aria-pressed={confidence === c.key}
								title={c.hint} onClick={() => setConfidence(c.key)}>
								{c.label}{n != null && <span className="seg__n">{n}</span>}
							</button>
						);
					})}
				</div>
				<input className="search" type="search" placeholder="Sök beskrivning, kund, regel…"
					value={q} onChange={e => setQ(e.target.value)} aria-label="Sök" />
				<span className="spacer" />
				<span className="stamp">
					{data.loading ? 'Hämtar…' : `${data.total.toLocaleString('sv-SE')} rader`}
					{summary?.manual ? ` · ${summary.manual} manuella` : ''}
				</span>
				<div className="pager">
					<button className="btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Föregående</button>
					<span className="stamp">{data.page} / {data.pages || 1}</span>
					<button className="btn" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}>Nästa</button>
				</div>
			</div>

			<div className="table-wrap">
				<table>
					<thead><tr>
						<th>Datum</th><th>Kund</th><th>Beskrivning</th><th>Belopp</th>
						<th>Gammal art.</th><th>Regel</th><th>Säkerhet</th><th>Artikel</th>
					</tr></thead>
					<tbody>
						{data.rows.length === 0 && !data.loading && (
							<tr><td colSpan={8} style={{textAlign: 'center', padding: 28}}>Inga rader</td></tr>
						)}
						{data.rows.map(r => (
							<tr key={r._id}>
								<td>{r.date}</td>
								<td>{(r.customer ?? '—').slice(0, 28)}</td>
								<td className="cl__desc" title={r.description}>{r.description}</td>
								<td className="num--strong">{kr(r.amount)}</td>
								<td>{r.legacy_article ?? '—'}</td>
								<td><code className="cl__rule">{r.rule ?? '—'}</code></td>
								<td>{r.manual ? <span className="pill">manuell</span>
									: (r.confidence ?? <span className="pill">oklassat</span>)}</td>
								<td>
									<select className="select select--sm" value={r.article ?? ''}
										disabled={saving === r._id}
										onChange={e => override(r._id, e.target.value)}>
										<option value="">— välj artikel —</option>
										{articles.map(a => (
											<option key={a._id} value={a._id}>{a._id} {a.name}</option>
										))}
									</select>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{summary && (
				<div className="table-wrap">
					<table style={{minWidth: 520}}>
						<thead><tr><th>Artikel</th><th>Benämning</th><th>Rader</th><th>Belopp</th></tr></thead>
						<tbody>
							{summary.by_article.map(a => (
								<tr key={a._id ?? 'null'}>
									<td>{a._id ?? '—'}</td><td>{a.name ?? 'OKLASSAT'}</td>
									<td>{a.n}</td><td>{kr(a.amount)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</>
	);
}
